import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useRecorder, formatTime } from "@/lib/useRecorder";
import {
  Microphone,
  Stop,
  Play,
  ArrowClockwise,
  ArrowRight,
  CheckCircle,
  Waveform as WaveIcon,
  Quotes,
  SpeakerHigh,
  EyeSlash,
  PauseCircle,
} from "@phosphor-icons/react";
import { INTERVIEW } from "@/constants/testIds";
import { makeT } from "@/lib/i18n";
import { SeniorityBadge } from "@/components/SeniorityBadge";
import { extForMime } from "@/lib/useRecorder";
import { LEAD_IN_VARIANTS, exchangeStage, isExchangeDone } from "@/lib/followUps";
import { FollowUpTranslation } from "@/components/QuestionTranslationAid";
import LiveInterview from "@/pages/LiveInterview";
import { AnswerScriptButton } from "@/components/AnswerScriptButton";

// Fixed, approved warm-up library. These are the ONLY warm-up questions that
// may be used — do NOT add, modify, or generate additional warm-up questions
// without explicit approval. One is picked at random for each session. The
// warm-up is a client-side practice exercise only; the response is NEVER
// sent to the backend and cannot appear in transcripts, scores, reports,
// or coaching.
const WARMUP_POOL = [
  "Describe your favorite season of the year and explain why you like it.",
  "Describe your favorite type of food and what you enjoy about it.",
  "Describe a place that you would recommend someone visit and explain why.",
  "Describe a piece of technology that you use regularly and why you find it useful.",
  "Describe your ideal vacation destination and what makes it appealing to you.",
];

const warmupKey = (id) => `mec_warmup_${id}`;
const questionAutoplayKey = (id) => `mec_question_autoplayed_${id}`;
// Backend allows 2 × 20 s attempts; give it headroom, then give up on audio.
const TTS_CLIENT_TIMEOUT_MS = 45000;
// "Replay slower" (Guided mode, 2026-10-26): the same clip at 75%, pitch kept. A
// per-question choice the candidate can use when they need it; questions always
// play first at normal speed.
const SLOW_PLAYBACK_RATE = 0.75;

function setPlaybackRate(el, rate) {
  el.playbackRate = rate;
  // Keep the interviewer's pitch: slower, not deeper.
  el.preservesPitch = true;
  el.webkitPreservesPitch = true;
}
// A transition clip that isn't ready quickly is skipped rather than delaying the next question.
const TRANSITION_FETCH_MS = 3000;
const TRANSITION_MAX_MS = 6000;
// The three "after an answer" phrases are used in turn (Admin → Voice Check).
const TRANSITION_VARIANTS = 3;


export default function Interview() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Snapshot the form state we were launched with so the "Reiniciar entrevista"
  // button can hand it back to the Upload screen — preserves name, resume,
  // JD selection, voice, etc. without re-uploading anything.
  const launchState = useRef(location.state?.formState || null);

  const restartInterview = () => {
    // Stop any playing TTS / recorder before leaving.
    try { rec.stop && rec.stop(); } catch { /* noop */ }
    try {
      const el = audioElRef.current;
      if (el) { el.pause(); el.currentTime = 0; }
    } catch { /* noop */ }
    if (!window.confirm(t("iv.restartConfirm"))) return;
    // Fire-and-forget deletion of the abandoned session — user shouldn't
    // wait on the network to leave the page. Completed sessions are kept
    // by the backend regardless.
    api.delete(`/sessions/${sessionId}`).catch(() => { /* noop */ });
    try { window.sessionStorage.removeItem(questionAutoplayKey(sessionId)); } catch { /* noop */ }
    navigate("/", { state: { restore: launchState.current || {} } });
  };
  const [session, setSession] = useState(null);
  // Live Interview mode (LIVE_INTERVIEW_PLAN.md §8): its own screen. "Continue in Guided"
  // after a microphone failure (L6) switches this page back to Guided for this visit.
  const [guidedInstead, setGuidedInstead] = useState(false);
  const liveMode = session?.mode === "live" && !guidedInstead;
  const [idx, setIdx] = useState(0);
  const [transcript, setTranscript] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [revealText, setRevealText] = useState(false);
  const [revealFollowUp, setRevealFollowUp] = useState(false);
  const rec = useRecorder();

  // Auto-advance timer: after a successful submission we wait ~2s so the
  // candidate registers that their answer was saved, then jump to the next
  // question. No banner, no undo — the pause itself is the affordance.
  const autoAdvanceTimerRef = useRef(null);
  const clearAutoAdvanceTimer = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, []);
  useEffect(() => () => clearAutoAdvanceTimer(), [clearAutoAdvanceTimer]);

  // Warm-up state — entirely client-side. sessionStorage keeps the flag
  // across page reloads for the same session but never persists to the
  // backend, so no warm-up data can leak into any interview artifact.
  const warmupRec = useRecorder();
  const [warmupDone, setWarmupDone] = useState(() => {
    try {
      return typeof window !== "undefined" && window.sessionStorage.getItem(warmupKey(sessionId)) === "1";
    } catch {
      return false;
    }
  });
  const [warmupQuestion] = useState(
    () => WARMUP_POOL[Math.floor(Math.random() * WARMUP_POOL.length)]
  );
  const warmupAudioUrl = useMemo(
    () => (warmupRec.blob ? URL.createObjectURL(warmupRec.blob) : null),
    [warmupRec.blob]
  );
  const finishWarmup = () => {
    try { warmupRec.stop && warmupRec.stop(); } catch { /* noop */ }
    try { window.sessionStorage.setItem(warmupKey(sessionId), "1"); } catch { /* noop */ }
    setWarmupDone(true);
  };

  // TTS playback state. Only the original approved English question is voiced.
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const audioElRef = useRef(null);
  const ttsCacheRef = useRef({}); // question index -> object URL
  const autoplayedRef = useRef(new Set());

  // Persist which questions have already auto-played for this interview in the
  // current browser tab. Temporary detours (Account Settings, Downloads, Help)
  // remount the interview component; without this marker the same unanswered
  // question would auto-play again and could trigger another TTS request.
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(questionAutoplayKey(sessionId));
      const stored = raw ? JSON.parse(raw) : [];
      autoplayedRef.current = new Set(
        Array.isArray(stored) ? stored.filter((value) => Number.isInteger(value)) : []
      );
    } catch {
      autoplayedRef.current = new Set();
    }
  }, [sessionId]);

  const rememberAutoplayedQuestion = useCallback((questionIndex) => {
    autoplayedRef.current.add(questionIndex);
    try {
      window.sessionStorage.setItem(
        questionAutoplayKey(sessionId),
        JSON.stringify(Array.from(autoplayedRef.current))
      );
    } catch { /* noop */ }
  }, [sessionId]);

  // Pending autoplay timer for the current question — used so a Record tap
  // can cancel it and prevent TTS from starting after recording has begun.
  const pendingAutoplayRef = useRef(null);
  const cancelPendingAutoplay = () => {
    if (pendingAutoplayRef.current) {
      clearTimeout(pendingAutoplayRef.current);
      pendingAutoplayRef.current = null;
    }
  };

  const audioUrl = useMemo(
    () => (rec.blob ? URL.createObjectURL(rec.blob) : null),
    [rec.blob]
  );
  // Whenever `audioUrl` changes (new recording OR blob cleared) revoke the
  // previous object URL so the underlying Blob can be garbage-collected.
  useEffect(() => {
    if (!audioUrl) return undefined;
    return () => URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  useEffect(() => {
    api
      .get(`/sessions/${sessionId}`)
      .then((r) => {
        setSession(r.data);
        // Jump to first unanswered question
        // An exchange waiting for a follow-up or a repeat isn't over yet.
        const answered = new Set((r.data.answers || []).filter(isExchangeDone).map((a) => a.question_index));
        const first = r.data.questions.findIndex((_, i) => !answered.has(i));
        setIdx(first === -1 ? r.data.questions.length - 1 : first);
      })
      .catch(() => {
        toast.error(t("iv.loadFail"));
        navigate("/");
      });
  }, [sessionId, navigate]);

  const question = session?.questions?.[idx];
  const total = session?.questions?.length || 0;
  const answers = session?.answers || [];
  const currentAnswer = answers.find((a) => a.question_index === idx);
  // Follow-ups (FOLLOW_UP_PLAN.md §9): after the main answer the exchange may wait
  // for a follow-up answer or a repeat of an answer that couldn't be understood.
  const stage = exchangeStage(currentAnswer);
  const exchangeDone = stage === "done";
  const pendingFollowUp = stage === "follow_up" ? currentAnswer.follow_up : null;
  const t = makeT(session?.interface_language || session?.language || "en");
  // With follow-ups on, Submit waits only for the check, not for scoring.
  const submittingLabel = session?.follow_ups_enabled ? t("iv.checking") : t("iv.submitting");

  const submit = async () => {
    if (!rec.blob) return;
    setSubmitting(true);
    // A follow-up's answer goes to its own endpoint; the exchange then moves on.
    const endpoint = pendingFollowUp ? "follow-up-answer" : "answer";
    const answeredIdx = idx;
    let outcome = null;
    try {
      const fd = new FormData();
      // Build the filename from the ACTUAL blob mime type — iOS Safari
      // records audio/mp4 (not webm), and Whisper needs the extension to
      // match the bytes it receives.
      const ext = extForMime(rec.blob.type);
      fd.append("audio", rec.blob, `answer.${ext}`);
      fd.append("question_index", String(idx));
      const { data } = await api.post(`/sessions/${sessionId}/${endpoint}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTranscript(data.transcript);
      // Audio has been successfully transcribed and evaluated by the
      // backend. Immediately release the local audio Blob so it cannot
      // remain in memory beyond the minimum time technically needed for
      // transcription/upload. Only the transcript + evaluation persist.
      rec.reset();
      // Refresh session state
      const s = await api.get(`/sessions/${sessionId}`);
      setSession(s.data);
      outcome = {
        action: data.action || "none", followUp: data.follow_up || null, total: s.data?.questions?.length || 0,
        // S5: after Question 10 the closing question comes before the end (not on a Free trial).
        closing: !s.data?.free_trial && !s.data?.closing,
      };
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || t("iv.submitFail"));
    } finally {
      setSubmitting(false);
    }
    if (!outcome) return;
    if (outcome.action === "follow_up" && outcome.followUp) {
      // F11: a lead-in, then the follow-up; both were fetched while the candidate
      // answered. Never over a candidate who started recording or moved on.
      interruptedRef.current = false;
      await playTransition("lead_in", answeredIdx % LEAD_IN_VARIANTS);
      if (!interruptedRef.current && idxRef.current === answeredIdx) {
        playQuestion(answeredIdx, { silent: true, followUpId: outcome.followUp.id });
      }
      return;
    }
    if (outcome.action === "repeat") {
      // F10: "I'm sorry, I didn't understand your response…"; the recorder is ready again.
      await playTransition("repeat");
      return;
    }
    toast.success(t("iv.answerSaved"));
    // The interviewer thanks the candidate, then moves on to the next question
    // ("Thank you, let's move on…"), or closes on the final one, where the user
    // still clicks Finish. Advance only from the question just answered, in case
    // the user moved on by hand while the clip played.
    const isLast = answeredIdx >= outcome.total - 1;
    await playTransition(isLast ? (outcome.closing ? "closing_question" : "end") : "next", answeredIdx % TRANSITION_VARIANTS);
    if (!isLast) {
      setIdx((i) => (i === answeredIdx && i < outcome.total - 1 ? i + 1 : i));
    }
  };

  // S5 (2026-11-01): the answer to "Do you have any questions for me?". The interviewer
  // never answers them: a neutral reply, then the usual end of the interview.
  const submitClosing = async (noAnswer = false) => {
    if (!noAnswer && !rec.blob) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (noAnswer) fd.append("no_answer", "true");
      else fd.append("audio", rec.blob, `closing.${extForMime(rec.blob.type)}`);
      const { data } = await api.post(`/sessions/${sessionId}/closing-answer`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      rec.reset();
      await playTransition(data.has_questions ? "closing_reply" : "closing_reply_none");
      await playTransition("end_after_closing");
      const s = await api.get(`/sessions/${sessionId}`);
      setSession(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || t("iv.submitFail"));
    } finally {
      setSubmitting(false);
    }
  };

  const finish = async () => {
    setFinishing(true);
    try {
      await api.post(`/sessions/${sessionId}/complete`);
      navigate(`/report/${sessionId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("iv.reportFail"));
      setFinishing(false);
    }
  };

  // Fetch/cache TTS for the approved English question. In-flight requests are
  // deduplicated so prefetch and play requests for the same question share one
  // network call.
  const ttsInflightRef = useRef({}); // question index (":follow-up id") -> Promise<url>
  const fetchTts = (i, followUpId = "") => {
    const key = followUpId ? `${i}:${followUpId}` : String(i);
    if (ttsCacheRef.current[key]) return Promise.resolve(ttsCacheRef.current[key]);
    if (ttsInflightRef.current[key]) return ttsInflightRef.current[key];
    const fd = new FormData();
    fd.append("question_index", String(i));
    fd.append("variant", "original");
    if (followUpId) fd.append("follow_up_id", followUpId);
    const p = api
      .post(`/sessions/${sessionId}/tts`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: TTS_CLIENT_TIMEOUT_MS,
      })
      .then(({ data }) => {
        const bin = atob(data.audio_base64);
        const bytes = new Uint8Array(bin.length);
        for (let k = 0; k < bin.length; k++) bytes[k] = bin.charCodeAt(k);
        const blob = new Blob([bytes], { type: data.mime || "audio/mpeg" });
        const url = URL.createObjectURL(blob);
        ttsCacheRef.current[key] = url;
        return url;
      })
      .finally(() => {
        delete ttsInflightRef.current[key];
      });
    ttsInflightRef.current[key] = p;
    return p;
  };

  const prefetchNextTts = (current) => {
    const next = current + 1;
    if (!session?.questions?.[next]) return;
    fetchTts(next).catch((e) => console.warn("TTS prefetch failed", next, e?.message));
  };

  // Spoken transitions ("next" after an answer, "end" after the last one), in the
  // session's interviewer voice. Fetched while the candidate answers, like the next
  // question, and kept for the rest of the interview.
  const transitionCacheRef = useRef({}); // "kind:variant" -> Promise<object URL>
  // The welcome must not start question 1 over a candidate who began recording
  // (or moved on) while it played.
  const welcomeInterruptedRef = useRef(false);
  // Same for the follow-up after its lead-in.
  const interruptedRef = useRef(false);
  const idxRef = useRef(idx);
  idxRef.current = idx;
  // `variant` picks one of the three "after an answer" phrases, used in turn by question.
  const fetchTransition = (kind, variant = 0) => {
    const cacheKey = `${kind}:${variant}`;
    if (!transitionCacheRef.current[cacheKey]) {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("variant", String(variant));
      transitionCacheRef.current[cacheKey] = api
        .post(`/sessions/${sessionId}/tts-transition`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: TTS_CLIENT_TIMEOUT_MS,
        })
        .then(({ data }) => {
          const bin = atob(data.audio_base64);
          const bytes = new Uint8Array(bin.length);
          for (let k = 0; k < bin.length; k++) bytes[k] = bin.charCodeAt(k);
          return URL.createObjectURL(new Blob([bytes], { type: data.mime || "audio/mpeg" }));
        })
        .catch((e) => {
          delete transitionCacheRef.current[cacheKey]; // try again next time
          throw e;
        });
    }
    return transitionCacheRef.current[cacheKey];
  };

  // Resolves when the clip ends. A missing or blocked clip is skipped, never
  // holding up the interview; TRANSITION_MAX_MS caps a clip that never reports
  // its end.
  const playTransition = async (kind, variant = 0) => {
    const el = audioElRef.current;
    if (!el) return;
    try {
      const url = await Promise.race([
        fetchTransition(kind, variant),
        new Promise((_, reject) => setTimeout(() => reject(new Error("transition timeout")), TRANSITION_FETCH_MS)),
      ]);
      await new Promise((resolve) => {
        const timer = setTimeout(done, TRANSITION_MAX_MS);
        function done() {
          clearTimeout(timer);
          el.removeEventListener("ended", done);
          el.removeEventListener("error", done);
          resolve();
        }
        el.addEventListener("ended", done);
        el.addEventListener("error", done);
        el.pause();
        el.src = url;
        el.currentTime = 0;
        setPlaybackRate(el, 1);
        el.play().catch(done);
      });
    } catch (e) {
      console.warn("Transition audio skipped", kind, e?.message);
    }
  };

  const playQuestion = async (i = idx, { silent = false, rate = 1, followUpId = "" } = {}) => {
    if (!session?.questions?.[i]) return;
    try {
      setTtsLoading(true);
      const url = await fetchTts(i, followUpId);
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current.src = url;
        audioElRef.current.currentTime = 0;
        setPlaybackRate(audioElRef.current, rate);
        await audioElRef.current.play();
        setTtsPlaying(true);
      }
    } catch (e) {
      console.warn("TTS play failed", e);
      const isAutoplayBlock = e?.name === "NotAllowedError";
      if (!isAutoplayBlock) {
        // Audio is unavailable (timeout / backend gave up / network): never
        // block the interview — make the question text readable and move on.
        if (followUpId) setRevealFollowUp(true);
        else setRevealText(true);
        toast.error(t("iv.audioUnavailable"));
      } else if (!silent) {
        toast.error(t("iv.audioPlayFail"));
      }
    } finally {
      setTtsLoading(false);
    }
  };

  const stopQuestion = () => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current.currentTime = 0;
    }
    setTtsPlaying(false);
  };

  // Wrap rec.start so any interviewer audio currently playing is silenced
  // immediately — recording always takes priority over TTS playback so the
  // interviewer voice can't bleed into the candidate's answer.
  const startRecording = () => {
    welcomeInterruptedRef.current = true;
    interruptedRef.current = true;
    cancelPendingAutoplay();
    stopQuestion();
    rec.start();
    // Warm the next approved question and the spoken transition while the
    // candidate is answering.
    prefetchNextTts(idx);
    if (session?.follow_ups_enabled && !pendingFollowUp) {
      // Everything the interviewer might say after this answer, so it plays at once:
      // each follow-up this question could ask, its lead-in, and the repeat request.
      (question?.follow_ups || []).forEach((f) => {
        if (f.id) fetchTts(idx, f.id).catch(() => {});
      });
      fetchTransition("lead_in", idx % LEAD_IN_VARIANTS).catch(() => {});
      fetchTransition("repeat").catch(() => {});
    }
    if (idx >= (session?.questions?.length || 0) - 1) fetchTransition("end").catch(() => {});
    else fetchTransition("next", idx % TRANSITION_VARIANTS).catch(() => {});
  };

  // Reset per-question UI state when index changes; auto-play each question
  // only the first time it is presented in this browser-tab interview session.
  useEffect(() => {
    setRevealText(false);
    setRevealFollowUp(false);
    setTranscript(null);
    rec.reset();
    stopQuestion();
    // Any pending auto-advance is bound to the previous question — clear it
    // if the user navigated (or the timer fired) so it can't race.
    clearAutoAdvanceTimer();
    if (!session || !session.questions?.[idx] || liveMode) return; // Live plays its own audio
    // The welcome is fetched while the warm-up is on screen, so it's ready for question 1.
    const firstPresentation = !autoplayedRef.current.has(idx);
    if (idx === 0 && firstPresentation) fetchTransition("welcome").catch(() => {});
    // Don't autoplay while the warm-up gate is still on screen — the main
    // interview <audio> element isn't mounted yet, so playback would fail
    // silently AND the question would be marked "already autoplayed",
    // preventing Q1 from ever autoplaying once the user continues.
    if (!warmupDone) return;
    // Don't auto-play a question that has already been presented in this tab,
    // including after a temporary navigation detour or browser refresh.
    if (autoplayedRef.current.has(idx)) return;
    const answered = (session.answers || []).some((a) => a.question_index === idx && isExchangeDone(a));
    if (answered) return;
    rememberAutoplayedQuestion(idx);
    const key = String(idx);
    // Prefetched audio plays immediately; otherwise keep the short settle delay.
    const delay = ttsCacheRef.current[key] ? 0 : 400;
    pendingAutoplayRef.current = setTimeout(async () => {
      pendingAutoplayRef.current = null;
      // "Welcome to your practice interview, <first name>." opens the interview,
      // once, just before question 1 is first presented.
      if (idx === 0) {
        welcomeInterruptedRef.current = false;
        await playTransition("welcome");
        // The candidate started recording, or moved on, during the welcome.
        if (welcomeInterruptedRef.current || idxRef.current !== 0) return;
      }
      playQuestion(idx, { silent: true });
    }, delay);
    return () => {
      if (pendingAutoplayRef.current) {
        clearTimeout(pendingAutoplayRef.current);
        pendingAutoplayRef.current = null;
      }
    };
  }, [idx, session?.session_id, warmupDone, guidedInstead, clearAutoAdvanceTimer, rememberAutoplayedQuestion]);

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[color:var(--text-secondary)]">
        {t("iv.loading")}
      </div>
    );
  }

  if (liveMode) {
    return <LiveInterview session={session} onUseGuided={() => setGuidedInstead(true)} />;
  }

  // Show the mandatory warm-up gate before the first real question. Once the
  // user completes it (or if they've already answered a real question, e.g.
  // after refreshing mid-interview) we fall through to the assessment UI.
  if (!warmupDone && (session.answers || []).length === 0) {
    return (
      <WarmupGate
        question={warmupQuestion}
        rec={warmupRec}
        audioUrl={warmupAudioUrl}
        onContinue={finishWarmup}
        onRecordStart={() => prefetchNextTts(-1)}
        voice={session.voice || "female"}
        t={t}
        sessionId={sessionId}
        lang={session.interface_language || session.language || "en"}
      />
    );
  }

  const answeredCount = answers.filter(isExchangeDone).length;
  const allAnswered = answeredCount === total;
  // S5: the closing question is waiting for an answer (never on a Free trial).
  const closingNeeded = allAnswered && !session.free_trial && !session.closing;
  const hasAnswerForCurrent = Boolean(currentAnswer);
  const hideText = Boolean(session.hide_text) && !revealText && !hasAnswerForCurrent;

  return (
    <div className="min-h-screen" data-testid={INTERVIEW.container}>
      <header className="border-b border-[color:var(--border-stone)] bg-white/70 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-8 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/brand/logo.png"
              alt="English Job Interview Simulator"
              className="h-[63px] w-[63px] object-contain"
            />
            <div className="leading-tight">
              <div className="flex items-center gap-2">
                <span className="font-semibold tracking-tight">English Job Interview Simulator</span>
              </div>
            </div>
            {session?.seniority?.level && (
              <div className="hidden sm:block ml-2">
                <SeniorityBadge
                  seniority={session.seniority}
                  lang={session.interface_language || session.language || "en"}
                  testId="interview-seniority-badge"
                />
              </div>
            )}
          </div>
          <ProgressRing
            current={idx + 1}
            total={total}
            answered={answeredCount}
            testId={INTERVIEW.progressLabel}
            t={t}
          />
          <Button
            data-testid={INTERVIEW.finishBtn}
            disabled={!allAnswered || finishing || closingNeeded}
            onClick={finish}
            className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white disabled:opacity-40"
          >
            {finishing ? t("iv.finish.busy") : t("iv.finish")}
          </Button>
        </div>
        <div className="h-1 w-full bg-[color:var(--border-stone)]">
          <div
            className="h-full bg-[color:var(--brand)] transition-all"
            style={{ width: `${(answeredCount / Math.max(1, total)) * 100}%` }}
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-8 md:px-12 py-12 pb-32 sm:pb-12">
        {/* One player for every question, transition and the closing question, outside
            both cards: removing a playing element from the page would pause it. */}
        <audio
          ref={audioElRef}
          onEnded={() => setTtsPlaying(false)}
          onPause={() => setTtsPlaying(false)}
          className="hidden"
        />
        {closingNeeded ? (
          <section data-testid="closing-question">
            <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)] mb-4">{t("iv.closingLabel")}</div>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-[42px] font-semibold tracking-tight leading-[1.15]">
              <Quotes size={28} weight="fill" className="inline-block text-[color:var(--brand)] mr-2 -translate-y-1" />
              Do you have any questions for me? {/* l10n-ignore: the interviewer's spoken question, English by design */}
            </h1>
            <p className="mt-4 text-[color:var(--text-secondary)] leading-relaxed max-w-2xl">{t("iv.closingHint")}</p>
            <div className="mt-10 rounded-3xl border border-[color:var(--border-stone)] bg-white p-8">
              <RecorderView
                t={t}
                recording={rec.recording}
                seconds={rec.seconds}
                blob={rec.blob}
                audioUrl={audioUrl}
                error={rec.error}
                onStart={startRecording}
                onStop={rec.stop}
                onReset={rec.reset}
                onSubmit={() => submitClosing(false)}
                submitting={submitting}
                submittingLabel={t("iv.submitting")}
                transcript=""
                notice={null}
              />
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                data-testid="closing-no-questions"
                onClick={() => submitClosing(true)}
                disabled={submitting || rec.recording}
                className="text-sm text-[color:var(--brand)] hover:underline disabled:opacity-40"
              >
                {t("iv.closingNone")}
              </button>
            </div>
          </section>
        ) : (
        <section>
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)]">
              {hideText
                ? t("iv.questionN", idx + 1)
                : question?.type === "standard"
                ? t("iv.standardQuestion")
                : question?.focus || (session.has_job_description ? t("iv.roleQuestion") : t("iv.resumeQuestion"))}
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                type="button"
                data-testid={INTERVIEW.playQuestion || "play-question-btn"}
                onClick={() => (ttsPlaying ? stopQuestion() : playQuestion(idx))}
                disabled={ttsLoading}
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)]/10 px-4 py-2 text-sm disabled:opacity-50"
              >
                {ttsLoading ? (
                  <>
                    <span className="h-2 w-2 rounded-full bg-[color:var(--brand)] animate-pulse" />
                    {t("iv.loading")}
                  </>
                ) : ttsPlaying ? (
                  <>
                    <PauseCircle weight="fill" size={16} /> {t("iv.stopQ")}
                  </>
                ) : (
                  <>
                    <SpeakerHigh weight="fill" size={16} /> {t("iv.playQ")}
                  </>
                )}
              </button>
              <button
                type="button"
                data-testid="play-question-slower-btn"
                onClick={() => {
                  stopQuestion();
                  playQuestion(idx, { rate: SLOW_PLAYBACK_RATE });
                }}
                disabled={ttsLoading}
                title="75%"
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border-stone)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-surface-hover)] hover:text-[color:var(--text-primary)] px-4 py-2 text-sm disabled:opacity-50"
              >
                <SpeakerHigh size={16} /> {t("iv.playSlow")}
              </button>
              <button
                type="button"
                data-testid="restart-interview-btn"
                onClick={restartInterview}
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border-stone)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-surface-hover)] hover:text-[color:var(--text-primary)] px-4 py-2 text-sm"
              >
                <ArrowClockwise size={16} /> {t("iv.restartInterview")}
              </button>
              <span className="text-xs mono text-[color:var(--text-secondary)] uppercase tracking-widest">
                {session.voice === "male" ? t("iv.maleVoice") : t("iv.femaleVoice")}
              </span>
            </div>
          </div>
          {hideText ? (
            <div className="mt-2 rounded-3xl border border-dashed border-[color:var(--brand)]/40 bg-[color:var(--brand)]/5 p-8">
              <div className="flex items-center gap-3 text-[color:var(--brand)]">
                <EyeSlash weight="duotone" size={22} />
                <div className="font-semibold tracking-tight">{t("iv.hiddenH")}</div>
              </div>
              <p className="mt-3 text-[color:var(--text-secondary)] leading-relaxed max-w-xl">
                {t("iv.hiddenP")}
              </p>
              <button
                data-testid="reveal-question-text"
                onClick={() => setRevealText(true)}
                className="mt-5 text-sm text-[color:var(--brand)] hover:underline"
              >
                {t("iv.reveal")}
              </button>
            </div>
          ) : (
            <h1
              className="mt-2 text-3xl sm:text-4xl lg:text-[42px] font-semibold tracking-tight leading-[1.15]"
              data-testid="question-text"
            >
              <Quotes size={28} weight="fill" className="inline-block text-[color:var(--brand)] mr-2 -translate-y-1" />
              {question?.text}
            </h1>
          )}

          {pendingFollowUp && (
            <div
              className="mt-6 rounded-3xl border-l-4 border-[color:var(--brand)] bg-[color:var(--brand)]/5 p-6"
              data-testid="follow-up-card"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)]">
                  {t("iv.followUpLabel")}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="play-follow-up-btn"
                    onClick={() => (ttsPlaying ? stopQuestion() : playQuestion(idx, { followUpId: pendingFollowUp.id }))}
                    disabled={ttsLoading}
                    className="inline-flex items-center gap-2 rounded-full border border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)]/10 px-4 py-2 text-sm disabled:opacity-50"
                  >
                    {ttsPlaying ? <PauseCircle weight="fill" size={16} /> : <SpeakerHigh weight="fill" size={16} />}
                    {ttsLoading ? t("iv.loading") : ttsPlaying ? t("iv.stopQ") : t("iv.playQ")}
                  </button>
                  <button
                    type="button"
                    data-testid="play-follow-up-slower-btn"
                    onClick={() => {
                      stopQuestion();
                      playQuestion(idx, { rate: SLOW_PLAYBACK_RATE, followUpId: pendingFollowUp.id });
                    }}
                    disabled={ttsLoading}
                    title="75%"
                    className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border-stone)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-surface-hover)] hover:text-[color:var(--text-primary)] px-4 py-2 text-sm disabled:opacity-50"
                  >
                    <SpeakerHigh size={16} /> {t("iv.playSlow")}
                  </button>
                </div>
              </div>
              {session.hide_text && !revealFollowUp ? (
                <div className="mt-3 text-[color:var(--text-secondary)]">
                  {t("iv.followUpHidden")}{" "}
                  <button
                    type="button"
                    data-testid="reveal-follow-up-text"
                    onClick={() => setRevealFollowUp(true)}
                    className="text-sm text-[color:var(--brand)] hover:underline"
                  >
                    {t("iv.reveal")}
                  </button>
                </div>
              ) : (
                <p className="mt-3 text-2xl font-semibold tracking-tight leading-snug" data-testid="follow-up-text">
                  {pendingFollowUp.text}
                </p>
              )}
              <p className="mt-2 text-sm text-[color:var(--text-secondary)]">{t("iv.followUpHint")}</p>
              <FollowUpTranslation sessionId={sessionId} questionIndex={idx} followUpId={pendingFollowUp.id} />
            </div>
          )}

          <div className="mt-10 rounded-3xl border border-[color:var(--border-stone)] bg-white p-8">
            {exchangeDone ? (
              <AnsweredView
                answer={currentAnswer}
                t={t}
                isFinal={idx === total - 1 && allAnswered}
              />
            ) : (
              <RecorderView
                t={t}
                recording={rec.recording}
                seconds={rec.seconds}
                blob={rec.blob}
                audioUrl={audioUrl}
                error={rec.error}
                onStart={startRecording}
                onStop={rec.stop}
                onReset={rec.reset}
                onSubmit={submit}
                submitting={submitting}
                submittingLabel={submittingLabel}
                transcript={transcript}
                notice={stage === "repeat" ? { title: t("iv.repeatH"), body: t("iv.repeatP") } : null}
              />
            )}
          </div>

          <div className="mt-8 flex items-center justify-end">
            {idx < total - 1 ? (
              <Button
                data-testid={INTERVIEW.nextBtn}
                onClick={() => setIdx(idx + 1)}
                disabled={!exchangeDone}
                variant="outline"
                className="rounded-full border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)]/10 disabled:opacity-40"
              >
                {t("iv.skipNext")}
                <ArrowRight className="ml-2" size={16} />
              </Button>
            ) : allAnswered ? (
              <Button
                data-testid="finish-inline-btn"
                onClick={finish}
                disabled={finishing}
                className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white disabled:opacity-40"
              >
                {finishing ? t("iv.finish.busy") : t("iv.finish")}
                {!finishing && <ArrowRight className="ml-2" size={16} />}
              </Button>
            ) : (
              <div className="text-sm text-[color:var(--text-secondary)]">
                {t("iv.lastHint")}
              </div>
            )}
          </div>
        </section>
        )}
      </main>

      {/* Mobile-only sticky bar with a contextual primary CTA that mirrors
          the state of the recorder. Hidden on ≥sm. Safe-area padding for
          notched iPhones. */}
      <div
        data-testid="interview-mobile-cta"
        className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[color:var(--border-stone)] px-4 py-3 sm:hidden"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {rec.recording ? (
          <Button
            data-testid="interview-mobile-stop"
            onClick={rec.stop}
            className="w-full rounded-full bg-[color:var(--score-bad)] hover:brightness-110 text-white py-6 text-base"
          >
            <Stop weight="fill" className="mr-2" size={18} /> {t("iv.stop")}
          </Button>
        ) : allAnswered && exchangeDone && !closingNeeded ? (
          <Button
            data-testid="interview-mobile-finish"
            onClick={finish}
            disabled={finishing}
            className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base disabled:opacity-40"
          >
            {finishing ? t("iv.finish.busy") : t("iv.finish")}
            {!finishing && <ArrowRight className="ml-2" size={18} />}
          </Button>
        ) : rec.blob ? (
          <div className="flex items-center gap-2">
            <button
              data-testid="interview-mobile-rerecord"
              onClick={rec.reset}
              className="shrink-0 h-12 w-12 rounded-full border border-[color:var(--border-stone)] flex items-center justify-center text-[color:var(--text-secondary)]"
              aria-label={t("iv.rerecord")}
            >
              <ArrowClockwise size={18} />
            </button>
            <Button
              data-testid="interview-mobile-submit"
              onClick={closingNeeded ? () => submitClosing(false) : submit}
              disabled={submitting}
              className="flex-1 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base"
            >
              {submitting ? submittingLabel : t("iv.submit")}
              {!submitting && <ArrowRight className="ml-2" size={18} />}
            </Button>
          </div>
        ) : idx < total - 1 && exchangeDone ? (
          <Button
            data-testid="interview-mobile-next"
            onClick={() => setIdx(idx + 1)}
            className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base"
          >
            {t("iv.skipNext")} <ArrowRight className="ml-2" size={18} />
          </Button>
        ) : (
          <Button
            data-testid="interview-mobile-record"
            onClick={startRecording}
            className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base"
          >
            <Microphone weight="fill" className="mr-2" size={18} />
            {t("iv.tapMic")}
          </Button>
        )}
      </div>
    </div>
  );
}

function RecorderView({
  t,
  recording,
  seconds,
  blob,
  audioUrl,
  error,
  onStart,
  onStop,
  onReset,
  onSubmit,
  submitting,
  submittingLabel,
  transcript,
  notice = null,
}) {
  return (
    <div>
      {notice && (
        <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="repeat-notice" role="status">
          <div className="font-semibold tracking-tight">{notice.title}</div>
          <p className="mt-1 text-sm text-[color:var(--text-secondary)]">{notice.body}</p>
        </div>
      )}
      <div className="flex items-center gap-6">
        <div className="relative">
          {recording && (
            <>
              <span className="absolute inset-0 rounded-full bg-[color:var(--brand)]/30 rec-ring" />
              <span
                className="absolute inset-0 rounded-full bg-[color:var(--brand)]/20 rec-ring"
                style={{ animationDelay: "600ms" }}
              />
            </>
          )}
          {!recording && !blob ? (
            <button
              data-testid={INTERVIEW.recordBtn}
              onClick={onStart}
              className="relative h-20 w-20 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_10px_30px_-10px_rgba(74,107,83,0.6)]"
              aria-label={t("iv.recordAria")}
            >
              <Microphone weight="fill" size={30} />
            </button>
          ) : recording ? (
            <button
              data-testid={INTERVIEW.stopBtn}
              onClick={onStop}
              className="relative h-20 w-20 rounded-full bg-[color:var(--score-bad)] hover:brightness-110 text-white flex items-center justify-center"
              aria-label={t("iv.stopAria")}
            >
              <Stop weight="fill" size={26} />
            </button>
          ) : (
            <div className="relative h-20 w-20 rounded-full bg-[color:var(--brand)]/10 text-[color:var(--brand)] flex items-center justify-center">
              <CheckCircle weight="duotone" size={30} />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)]">
            {recording ? t("iv.recording") : blob ? t("iv.recorded") : t("iv.tapMic")}
          </div>
          <div className="mt-1 mono text-3xl">{formatTime(seconds)}</div>
          {recording && (
            <div className="mt-3 flex items-end gap-1 h-6">
              {Array.from({ length: 14 }).map((_, i) => (
                <span
                  key={i}
                  className="w-1.5 rounded-full bg-[color:var(--brand)] wave-bar"
                  style={{
                    height: `${30 + ((i * 7) % 70)}%`,
                    animationDelay: `${i * 60}ms`,
                  }}
                />
              ))}
            </div>
          )}
          {audioUrl && !recording && (
            <audio
              data-testid={INTERVIEW.playbackBtn}
              controls
              src={audioUrl}
              className="mt-3 w-full"
            />
          )}
          {error && (
            <div className="mt-3 text-sm text-[color:var(--score-bad)]">
              {error}. {t("iv.micAccessDenied")}
            </div>
          )}
        </div>
      </div>

      {blob && !recording && (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            data-testid={INTERVIEW.reRecordBtn}
            variant="outline"
            onClick={onReset}
            className="rounded-full border-[color:var(--border-stone)]"
          >
            <ArrowClockwise className="mr-2" size={16} /> {t("iv.rerecord")}
          </Button>
          <Button
            data-testid={INTERVIEW.submitBtn}
            onClick={onSubmit}
            disabled={submitting}
            className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white"
          >
            {submitting ? submittingLabel || t("iv.submitting") : t("iv.submit")}
            {!submitting && <ArrowRight className="ml-2" size={16} />}
          </Button>
        </div>
      )}
    </div>
  );
}

function AnsweredView({ answer, t, isFinal = false }) {
  if (isFinal) {
    return (
      <div data-testid="interview-complete-banner">
        <div className="flex items-start gap-4">
          <div className="text-3xl leading-none select-none" aria-hidden="true">
            🎉
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight text-[color:var(--text-primary)]">
              {t("iv.interviewCompleteTitle")}
            </div>
            <p className="mt-2 text-[color:var(--text-secondary)] leading-relaxed max-w-2xl">
              {t("iv.interviewCompleteBody")}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-[color:var(--brand)]/10 text-[color:var(--brand)] flex items-center justify-center">
          <CheckCircle weight="duotone" size={22} />
        </div>
        <div>
          <div className="font-semibold tracking-tight">{t("iv.answered")}</div>
          <div className="text-xs text-[color:var(--text-secondary)]">
            {t("iv.answeredSub")}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProgressRing({ current, total, answered, testId, t }) {
  const size = 44;
  const stroke = 3.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const safeTotal = Math.max(1, total);
  const clamped = Math.min(Math.max(current, 0), safeTotal);
  const pct = clamped / safeTotal;
  const dashOffset = circumference * (1 - pct);

  return (
    <div
      className="flex items-center gap-3"
      data-testid={testId}
      aria-label={t("iv.progressCurrent", clamped, safeTotal)}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--border-stone)"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--brand)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 400ms ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold tracking-tight text-[color:var(--text-primary)]">
          {clamped}/{safeTotal}
        </div>
      </div>
      <div className="leading-tight hidden sm:block">
        <div className="text-sm text-[color:var(--text-primary)]">
          {t("iv.progressCurrent", clamped, safeTotal)}
        </div>
        <div className="text-[11px] text-[color:var(--text-secondary)]">
          {t("iv.progressAnswered", answered)}
        </div>
      </div>
    </div>
  );
}

function WarmupGate({ question, rec, audioUrl, onContinue, onRecordStart, voice, t, sessionId, lang }) {
  const audioElRef = useRef(null);
  const audioUrlRef = useRef(null);
  const autoplayedRef = useRef(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsPlaying, setTtsPlaying] = useState(false);

  const fetchTts = useCallback(async () => {
    if (audioUrlRef.current) return audioUrlRef.current;
    const fd = new FormData();
    fd.append("text", question);
    fd.append("voice", voice || "female");
    const { data } = await api.post(`/tts/warmup`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: TTS_CLIENT_TIMEOUT_MS,
    });
    const bin = atob(data.audio_base64);
    const bytes = new Uint8Array(bin.length);
    for (let k = 0; k < bin.length; k++) bytes[k] = bin.charCodeAt(k);
    const blob = new Blob([bytes], { type: data.mime || "audio/mpeg" });
    const url = URL.createObjectURL(blob);
    audioUrlRef.current = url;
    return url;
  }, [question, voice]);

  const playQuestion = useCallback(async () => {
    try {
      setTtsLoading(true);
      const url = await fetchTts();
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current.src = url;
        audioElRef.current.currentTime = 0;
        try {
          await audioElRef.current.play();
          setTtsPlaying(true);
        } catch (playErr) {
          // NotAllowedError is Chrome/Safari's autoplay policy blocking
          // the initial mount call — expected before the user gestures
          // on the page. Silently swallow; the "Play question" button
          // remains available and will play on click (user gesture).
          if (!playErr || playErr.name !== "NotAllowedError") {
            console.error("warmup TTS play failed:", playErr);
            toast.error(t("iv.errCreateSession") || "Audio playback failed.");
          }
        }
      }
    } catch (err) {
      console.error("warmup TTS fetch failed:", err);
      toast.error(t("iv.audioUnavailable"));
    } finally {
      setTtsLoading(false);
    }
  }, [fetchTts, t]);

  const stopQuestion = () => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current.currentTime = 0;
    }
    setTtsPlaying(false);
  };

  // Wrap rec.start so any warm-up audio currently playing is silenced
  // immediately — recording always takes priority over TTS playback.
  const startRecording = () => {
    stopQuestion();
    rec.start();
    if (onRecordStart) onRecordStart();
  };

  // Autoplay the warm-up question once, using the selected interviewer
  // voice — mirroring how real interview questions are announced. We kick
  // off the TTS fetch immediately on mount so the audio starts as soon as
  // the OpenAI response arrives. Modern browsers may block this initial
  // play() until the user interacts with the page; that's not an error —
  // the visible "Play question" button remains available and works on
  // first click (a real user gesture).
  useEffect(() => {
    if (autoplayedRef.current) return;
    autoplayedRef.current = true;
    playQuestion();
  }, [playQuestion]);

  // Release the TTS blob URL when the gate unmounts.
  useEffect(() => () => {
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
  }, []);

  return (
    <div className="min-h-screen" data-testid="warmup-gate">
      <header className="border-b border-[color:var(--border-stone)] bg-white/70 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-8 md:px-12 py-5 flex items-center gap-3">
          <img
            src="/brand/logo.png"
            alt="English Job Interview Simulator"
            className="h-[63px] w-[63px] object-contain"
          />
          <div className="leading-tight">
            <div className="flex items-center gap-2">
              <span className="font-semibold tracking-tight">English Job Interview Simulator</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-8 md:px-12 py-12 pb-32 sm:pb-12">
        <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)] mb-3">
          {t("iv.warmupEye")}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.15]"
          data-testid="warmup-title"
        >
          {t("iv.warmupTitle")}
        </h1>
        <p
          className="mt-4 text-[color:var(--text-secondary)] leading-relaxed"
          data-testid="warmup-intro"
        >
          {t("iv.warmupIntro")}
        </p>
        <AnswerScriptButton sessionId={sessionId} lang={lang} className="mt-4" />

        <div className="mt-8 rounded-3xl border border-[color:var(--brand)]/30 bg-[color:var(--brand)]/5 p-6 sm:p-8">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--brand)]">
              {t("iv.warmupQuestionEye")}
            </div>
            <button
              type="button"
              data-testid="warmup-play-question"
              onClick={() => (ttsPlaying ? stopQuestion() : playQuestion())}
              disabled={ttsLoading}
              className="inline-flex items-center gap-2 rounded-full border border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)]/10 px-3 py-1.5 text-xs disabled:opacity-50"
            >
              {ttsLoading ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-[color:var(--brand)] animate-pulse" />
                  {t("iv.loading")}
                </>
              ) : ttsPlaying ? (
                <>
                  <PauseCircle weight="fill" size={14} /> {t("iv.stop")}
                </>
              ) : (
                <>
                  <SpeakerHigh weight="fill" size={14} /> {t("iv.playQ")}
                </>
              )}
            </button>
          </div>
          <div
            className="text-xl sm:text-2xl font-medium tracking-tight leading-snug"
            data-testid="warmup-question"
          >
            <Quotes size={22} weight="fill" className="inline-block text-[color:var(--brand)] mr-2 -translate-y-1" />
            {question}
          </div>
          <audio
            ref={audioElRef}
            preload="auto"
            onPlay={() => setTtsPlaying(true)}
            onEnded={() => setTtsPlaying(false)}
            onPause={() => setTtsPlaying(false)}
            className="hidden"
          />
        </div>

        <div className="mt-8 rounded-3xl border border-[color:var(--border-stone)] bg-white p-6 sm:p-8">
          {rec.error && (
            <div className="mb-4 text-sm text-[color:var(--score-bad)]" data-testid="warmup-error">
              {rec.error}
            </div>
          )}
          <div className="flex items-center gap-4">
            {rec.recording ? (
              <Button
                data-testid="warmup-stop"
                onClick={rec.stop}
                className="rounded-full bg-[color:var(--score-bad)] hover:brightness-110 text-white px-6"
              >
                <Stop weight="fill" className="mr-2" size={16} />
                {t("iv.stop")} · {formatTime(rec.seconds)}
              </Button>
            ) : rec.blob ? (
              <>
                <audio src={audioUrl} controls className="flex-1" data-testid="warmup-playback" />
                <Button
                  data-testid="warmup-rerecord"
                  variant="outline"
                  onClick={rec.reset}
                  className="rounded-full border border-[color:var(--border-stone)]"
                >
                  <ArrowClockwise className="mr-2" size={16} />
                  {t("iv.rerecord")}
                </Button>
              </>
            ) : (
              <Button
                data-testid="warmup-start"
                onClick={startRecording}
                className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-6"
              >
                <Microphone weight="fill" className="mr-2" size={16} />
                {t("iv.warmupStart")}
              </Button>
            )}
          </div>
          <p className="mt-4 text-xs text-[color:var(--text-secondary)]">
            {t("iv.warmupBanner")}
          </p>
        </div>

        <div className="mt-8 flex items-center justify-end">
          <Button
            data-testid="warmup-continue"
            onClick={onContinue}
            disabled={!rec.blob || rec.recording}
            className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-8 py-6 text-base disabled:opacity-40"
          >
            {t("iv.warmupContinue")}
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </main>

      <div
        data-testid="warmup-mobile-cta"
        className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[color:var(--border-stone)] px-4 py-3 sm:hidden"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {rec.recording ? (
          <Button
            data-testid="warmup-mobile-stop"
            onClick={rec.stop}
            className="w-full rounded-full bg-[color:var(--score-bad)] hover:brightness-110 text-white py-6 text-base"
          >
            <Stop weight="fill" className="mr-2" size={18} />
            {t("iv.stop")} · {formatTime(rec.seconds)}
          </Button>
        ) : rec.blob ? (
          <Button
            data-testid="warmup-mobile-continue"
            onClick={onContinue}
            className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base"
          >
            {t("iv.warmupContinue")}
            <ArrowRight className="ml-2" size={18} />
          </Button>
        ) : (
          <Button
            data-testid="warmup-mobile-start"
            onClick={startRecording}
            className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white py-6 text-base"
          >
            <Microphone weight="fill" className="mr-2" size={18} />
            {t("iv.warmupStart")}
          </Button>
        )}
      </div>
    </div>
  );
}